import type { PageDefinition } from '@/types/page'
import {
  ASSET_STATUS,
  LINK_STATUS,
  MEMBERSHIP_PLAN_STATUS,
  ORDER_STATUS,
  toOptions,
} from './common'

export const USER_PAGE_OPTIONS = [
  { value: 'pages/home/index', label: '首页' },
  { value: 'pages/medication/plan/index', label: '用药计划' },
  { value: 'pages/medication/add/index', label: '添加用药' },
  { value: 'pages/medication/manage/index', label: '用药管理' },
  { value: 'pages/medication/detail/index', label: '用药详情' },
  { value: 'pages/medication/records/index', label: '用药记录' },
  { value: 'pages/health/dashboard/index', label: '健康看板' },
  { value: 'pages/health/record/index', label: '记录健康数据' },
  { value: 'pages/health/device/index', label: '设备管理' },
  { value: 'pages/family/index/index', label: '家庭档案' },
  { value: 'pages/family/detail/index', label: '家庭详情' },
  { value: 'pages/family/add/index', label: '添加家庭成员' },
  { value: 'pages/family/alerts/index', label: '家庭预警' },
  { value: 'pages/profile/index/index', label: '个人中心' },
  { value: 'pages/profile/archive/index', label: '健康档案' },
  { value: 'pages/profile/report/index', label: '健康报告' },
  { value: 'pages/profile/notifications/index', label: '消息通知' },
  { value: 'pages/profile/privacy/index', label: '隐私与授权' },
  { value: 'pages/profile/help/index', label: '帮助中心' },
  { value: 'pages/system/settings/index', label: '系统设置' },
  { value: 'pages/system/not-found/index', label: '页面不存在' },
]

const BUSINESS_ACTIONS = [
  '打开用药提醒',
  '打开健康记录',
  '打开家庭邀请',
  '打开会员购买',
  '打开客服会话',
  '打开设备绑定',
].map(value => ({ value, label: value }))

export const membershipPlansPage: PageDefinition = {
  id: 'membership-plans',
  route: '#/membership/plans',
  title: '会员方案',
  purpose: '配置会员周期、价格、权益、封面与上下线状态，支撑用户端会员入口和家庭服务转化',
  group: '运营中心',
  icon: 'crown',
  kind: 'table',
  permission: 'membership.view',
  permissions: { manage: 'membership.manage', plan: 'membership.plan', export: 'membership.export' },
  searchKeys: ['plan_no', 'name', 'description', 'benefit_names'],
  statusKey: 'status',
  statusMap: MEMBERSHIP_PLAN_STATUS,
  timeField: 'updated_at',
  summary: [
    { key: 'total', label: '方案总数', kind: 'count' },
    { key: 'online', label: '在线方案', kind: 'count', where: { status: 'online' } },
    { key: 'sales', label: '累计购买', kind: 'sum', field: 'sales_count', suffix: ' 单', hint: '按已上线与已下线方案累计' },
    { key: 'benefits', label: '权益条目', kind: 'distinct', field: 'benefit_names', suffix: ' 项', hint: '来自权益目录' },
  ],
  columns: [
    { key: 'name', label: '方案名称', type: 'primary', subtitleKey: 'plan_no', minWidth: 200, sortable: true },
    { key: 'period_months', label: '周期', type: 'number', width: 74, align: 'center', suffix: ' 个月' },
    { key: 'price_fen', label: '价格', type: 'money', width: 106, align: 'right' },
    { key: 'benefit_names', label: '包含权益', type: 'tag', minWidth: 280 },
    { key: 'sales_count', label: '购买量', type: 'number', width: 86, align: 'right' },
    { key: 'status', label: '状态', type: 'status', width: 86 },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 150 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '方案名称、编号或权益', width: 230 },
    { key: 'status', label: '上线状态', type: 'select', options: toOptions(MEMBERSHIP_PLAN_STATUS), width: 108 },
    { key: 'period_months', label: '会员周期', type: 'select', options: [
      { value: '1', label: '1个月' },
      { value: '3', label: '3个月' },
      { value: '12', label: '12个月' },
    ], width: 110 },
    { key: 'updated_at', label: '更新时间', type: 'daterange', timeField: 'updated_at', width: 230 },
  ],
  formFields: [
    { key: 'plan_no', label: '方案编号', type: 'readonly', readonlyOnEdit: true, help: '保存后由系统生成，不可修改', placeholder: '保存后自动生成' },
    { key: 'name', label: '方案名称', type: 'text', required: true, maxLength: 30 },
    { key: 'period_months', label: '会员周期', type: 'select', options: [
      { value: '1', label: '1个月' },
      { value: '3', label: '3个月' },
      { value: '6', label: '6个月' },
      { value: '12', label: '12个月' },
    ], required: true },
    { key: 'price_fen', label: '方案价格', type: 'money', required: true, min: 0, help: '用户端支付金额，退款按订单实付计算' },
    { key: 'benefit_names', label: '包含权益', type: 'multiselect', optionSource: 'benefits', required: true },
    { key: 'description', label: '方案说明', type: 'textarea', full: true, maxLength: 200 },
    { key: 'cover_name', label: '方案封面', type: 'select', optionSource: 'assets', help: '来自附件库，比例 4:3' },
    { key: 'status', label: '上线状态', type: 'select', options: toOptions(MEMBERSHIP_PLAN_STATUS), defaultValue: 'online' },
  ],
  createLabel: '新增方案',
  createPermission: 'membership.plan',
  editPermission: 'membership.manage',
  exportPermission: 'membership.export',
  exportScope: 'filtered',
  extraActions: [
    { key: 'benefits', label: '权益目录', icon: 'gift', kind: 'link', to: '#/membership/plans?panel=benefits', permission: 'membership.plan' },
    { key: 'orders', label: '会员订单', icon: 'order', kind: 'link', to: '#/membership/orders', permission: 'membership.order' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'membership.manage', tone: 'primary' },
    { key: 'copy', label: '复制方案', permission: 'membership.plan' },
    { key: 'delete', label: '删除', permission: 'membership.plan', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['offline'] }, confirmText: '仅已下线且从未售出的方案可以删除；历史订单不会受影响。' },
    { key: 'offline', label: '下线', permission: 'membership.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['online'] } },
    { key: 'online', label: '上线', permission: 'membership.manage', visibleWhen: { field: 'status', in: ['offline'] } },
  ],
  detailSections: [
    { title: '方案内容', fields: ['plan_no', 'name', 'period_months', 'price_fen', 'benefit_names', 'description', 'cover_name'] },
    { title: '运营情况', fields: ['status', 'version', 'sales_count', 'created_at', 'updated_at'] },
  ],
  detailMetrics: [
    { label: '上线状态', field: 'status', type: 'status' },
    { label: '累计购买', field: 'sales_count', type: 'number', suffix: ' 单' },
    { label: '方案版本', field: 'version', type: 'number' },
  ],
  notice: '方案下线不影响已支付订单的会员有效期；价格与权益变更只对新订单生效。',
  emptyHint: '当前筛选下没有会员方案。',
  seedKey: 'membership_plans',
}

export const membershipOrdersPage: PageDefinition = {
  id: 'membership-orders',
  route: '#/membership/orders',
  title: '会员订单',
  purpose: '查看会员购买、支付、退款与关闭状态，处理支付售后并关联客服工单',
  group: '运营中心',
  icon: 'order',
  kind: 'table',
  permission: 'membership.view',
  permissions: { order: 'membership.order', refund: 'membership.refund', export: 'membership.export' },
  readOnly: true,
  hideSelection: true,
  searchKeys: ['order_no', 'user_name', 'plan_name', 'refund_reason'],
  statusKey: 'status',
  statusMap: ORDER_STATUS,
  timeField: 'created_at',
  summary: [
    { key: 'total', label: '订单总数', kind: 'count', hint: '近 120 天会员订单' },
    { key: 'paid', label: '已支付', kind: 'count', where: { status: 'paid' }, hint: '支付成功且未退款' },
    { key: 'refund', label: '退款处理中', kind: 'count', where: { status: 'refund_pending' }, hint: '需客服与运营协同处理' },
    { key: 'amount', label: '累计实付', kind: 'money', field: 'amount_fen', where: { status: 'paid' }, hint: '按已支付订单实收金额汇总' },
  ],
  columns: [
    { key: 'order_no', label: '订单编号', type: 'primary', subtitleKey: 'plan_name', width: 180, sortable: true },
    { key: 'user_name', label: '用户', minWidth: 100 },
    { key: 'amount_fen', label: '实付金额', type: 'money', width: 112, align: 'right' },
    { key: 'channel', label: '支付渠道', width: 96 },
    { key: 'status', label: '订单状态', type: 'status', width: 106 },
    { key: 'paid_at', label: '支付时间', type: 'datetime', width: 150 },
    { key: 'refund_amount_fen', label: '退款金额', type: 'money', width: 112, align: 'right' },
    { key: 'created_at', label: '创建时间', type: 'datetime', width: 150, sortable: true },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '订单编号、用户或方案', width: 230 },
    { key: 'status', label: '订单状态', type: 'select', options: toOptions(ORDER_STATUS), width: 116 },
    { key: 'plan_name', label: '会员方案', type: 'select', optionSource: 'membership_plans', width: 142 },
    { key: 'channel', label: '支付渠道', type: 'select', options: ['微信支付', '支付宝', '银联'].map(value => ({ value, label: value })), width: 112 },
    { key: 'created_at', label: '创建时间', type: 'daterange', timeField: 'created_at', width: 230 },
  ],
  exportPermission: 'membership.export',
  exportScope: 'filtered',
  rowActions: [
    { key: 'view', label: '详情' },
    {
      key: 'refund',
      label: '处理退款',
      permission: 'membership.refund',
      tone: 'danger',
      confirm: true,
      form: [{ key: 'refund_reason', label: '退款说明', type: 'textarea', required: true, full: true }],
      visibleWhen: { field: 'status', in: ['paid', 'refund_pending'] },
    },
    { key: 'create-ticket', label: '关联工单', permission: 'support.handle' },
    { key: 'close', label: '关闭订单', permission: 'membership.order', visibleWhen: { field: 'status', in: ['pending'] } },
  ],
  detailSections: [
    { title: '订单信息', fields: ['order_no', 'user_name', 'plan_name', 'amount_fen', 'channel', 'status'] },
    { title: '支付与退款', fields: ['paid_at', 'refund_amount_fen', 'refund_reason', 'closed_at', 'created_at', 'updated_at'] },
  ],
  detailMetrics: [
    { label: '实付金额', field: 'amount_fen', type: 'money' },
    { label: '退款金额', field: 'refund_amount_fen', type: 'money' },
    { label: '订单状态', field: 'status', type: 'status' },
  ],
  notice: '退款操作会记录操作人、原因和时间；已退款订单不可重复退款，会员权益按退款规则回收。',
  emptyHint: '当前筛选下没有会员订单。',
  seedKey: 'orders',
}

export const linksPage: PageDefinition = {
  id: 'links',
  route: '#/links',
  title: '链接管理',
  purpose: '统一管理用户端页面、业务动作与外部链接，供装修、消息、贴士和帮助中心复用',
  group: '运营中心',
  icon: 'link',
  kind: 'table',
  permission: 'links.view',
  permissions: { manage: 'links.manage' },
  searchKeys: ['name', 'category_name', 'target_type', 'target_page', 'business_action', 'external_url'],
  statusKey: 'status',
  statusMap: LINK_STATUS,
  timeField: 'updated_at',
  summary: [
    { key: 'total', label: '链接总数', kind: 'count' },
    { key: 'active', label: '启用链接', kind: 'count', where: { status: 'active' } },
    { key: 'pages', label: '用户端页面', kind: 'count', where: { target_type: '用户端页面' } },
    { key: 'usage', label: '累计引用', kind: 'sum', field: 'usage_count', suffix: ' 处' },
  ],
  columns: [
    { key: 'name', label: '链接名称', type: 'primary', subtitleKey: 'category_name', minWidth: 190, sortable: true },
    { key: 'target_type', label: '目标类型', type: 'tag', width: 104 },
    { key: 'target_page', label: '用户端页面', minWidth: 170, options: USER_PAGE_OPTIONS },
    { key: 'business_action', label: '业务动作', minWidth: 130 },
    { key: 'status', label: '状态', type: 'status', width: 80 },
    { key: 'usage_count', label: '引用次数', type: 'number', width: 88, align: 'right' },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 150 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '链接名称、页面或分类', width: 230 },
    { key: 'category_name', label: '链接分类', type: 'select', optionSource: 'link_categories', width: 126 },
    { key: 'target_type', label: '目标类型', type: 'select', options: ['用户端页面', '业务动作', '外部链接'].map(value => ({ value, label: value })), width: 112 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(LINK_STATUS), width: 100 },
    { key: 'updated_at', label: '更新时间', type: 'daterange', timeField: 'updated_at', width: 230 },
  ],
  formFields: [
    { key: 'name', label: '链接名称', type: 'text', required: true, maxLength: 40, placeholder: '供运营选择的业务名称' },
    { key: 'category_name', label: '链接分类', type: 'select', optionSource: 'link_categories', required: true },
    { key: 'target_type', label: '目标类型', type: 'select', options: ['用户端页面', '业务动作', '外部链接'].map(value => ({ value, label: value })), required: true },
    { key: 'target_page', label: '用户端页面', type: 'select', options: USER_PAGE_OPTIONS, help: '目标必须命中用户端真实页面注册表' },
    { key: 'business_action', label: '业务动作', type: 'select', options: BUSINESS_ACTIONS },
    { key: 'external_url', label: '外部链接', type: 'text', pattern: '^https://.+', patternMessage: '外部链接必须使用 HTTPS 且域名在白名单内', help: '仅允许 example.com 与 kanghu.example 白名单域名', full: true },
    { key: 'params', label: '附加参数', type: 'textarea', full: true, maxLength: 200, placeholder: 'JSON 格式，例如 {"source":"admin"}', help: '参数键必须在用户端白名单内，保存时校验 JSON 格式' },
    { key: 'status', label: '状态', type: 'select', options: toOptions(LINK_STATUS), defaultValue: 'active' },
  ],
  createLabel: '新增链接',
  createPermission: 'links.manage',
  editPermission: 'links.manage',
  exportScope: 'none',
  extraActions: [
    { key: 'categories', label: '链接分类', icon: 'layers', kind: 'link', to: '#/links?panel=categories', permission: 'links.manage' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'links.manage', tone: 'primary' },
    { key: 'toggle', label: '停用', permission: 'links.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['active'] } },
    { key: 'toggle', label: '启用', permission: 'links.manage', visibleWhen: { field: 'status', in: ['disabled'] } },
    { key: 'delete', label: '删除', permission: 'links.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'usage_count', in: [0, '0'] }, confirmText: '仅引用次数为 0 的链接可以删除。' },
  ],
  detailSections: [
    { title: '链接定义', fields: ['name', 'category_name', 'target_type', 'target_page', 'business_action', 'external_url', 'params'] },
    { title: '引用情况', fields: ['status', 'usage_count', 'created_at', 'updated_at'] },
  ],
  notice: '停用仍被装修、消息或内容引用的链接会提示引用冲突；用户端页面目标必须来自真实页面注册表。',
  emptyHint: '当前筛选下没有受管链接。',
  seedKey: 'links',
}

export const assetsPage: PageDefinition = {
  id: 'assets',
  route: '#/assets',
  title: '附件库',
  purpose: '统一管理头像、药品图、内容封面、报告模板等素材及其引用关系、归档状态',
  group: '运营中心',
  icon: 'image',
  kind: 'grid',
  permission: 'assets.view',
  permissions: { upload: 'assets.upload', replace: 'assets.replace', delete: 'assets.delete' },
  searchKeys: ['name', 'folder', 'references', 'asset_type'],
  statusKey: 'status',
  statusMap: ASSET_STATUS,
  timeField: 'uploaded_at',
  summary: [
    { key: 'total', label: '素材总数', kind: 'count' },
    { key: 'images', label: '图片素材', kind: 'count', where: { asset_type: '图片' } },
    { key: 'docs', label: '文档与视频', kind: 'count', where: { asset_type: '文档' } },
    { key: 'usage', label: '累计引用', kind: 'sum', field: 'usage_count', suffix: ' 处' },
  ],
  columns: [
    { key: 'name', label: '素材名称', type: 'primary', subtitleKey: 'references', minWidth: 210 },
    { key: 'asset_type', label: '类型', type: 'tag', width: 84 },
    { key: 'folder', label: '所属目录', width: 108 },
    { key: 'size_kb', label: '大小', type: 'number', width: 90, align: 'right', suffix: ' KB' },
    { key: 'usage_count', label: '引用', type: 'number', width: 70, align: 'right' },
    { key: 'status', label: '状态', type: 'status', width: 82 },
    { key: 'uploaded_at', label: '上传时间', type: 'datetime', width: 150 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '素材名称或引用位置', width: 230 },
    { key: 'folder', label: '所属目录', type: 'select', optionSource: 'asset_folders', width: 124 },
    { key: 'asset_type', label: '素材类型', type: 'select', options: ['图片', '文档', '视频'].map(value => ({ value, label: value })), width: 106 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(ASSET_STATUS), width: 100 },
    { key: 'uploaded_at', label: '上传时间', type: 'daterange', timeField: 'uploaded_at', width: 230 },
  ],
  createLabel: '上传素材',
  createPermission: 'assets.upload',
  exportScope: 'none',
  formFields: [
    { key: 'file', label: '选择文件', type: 'file', required: true, full: true, help: '支持 PNG、JPG、WEBP、PDF、MP4，图片建议不超过 5MB' },
    { key: 'name', label: '素材名称', type: 'text', required: true, maxLength: 60, placeholder: '用于运营检索的中文名称' },
    { key: 'folder', label: '所属目录', type: 'select', options: ['首页素材', '内容封面', '药品资料', '帮助中心'].map(value => ({ value, label: value })), required: true },
    { key: 'asset_type', label: '素材类型', type: 'select', options: ['图片', '文档', '视频'].map(value => ({ value, label: value })), required: true },
    { key: 'alt_text', label: '替代文本', type: 'text', full: true, maxLength: 120, help: '供无障碍阅读与图片加载失败时使用' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'replace', label: '替换', permission: 'assets.replace', confirm: true },
    { key: 'archive', label: '归档', permission: 'assets.delete', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['active'] } },
  ],
  detailSections: [
    { title: '素材信息', fields: ['name', 'asset_type', 'mime_type', 'size_kb', 'width', 'height', 'folder'] },
    { title: '引用与状态', fields: ['references', 'usage_count', 'status', 'uploaded_at', 'updated_at'] },
  ],
  notice: '删除或归档被引用的素材会显示引用位置；替换素材会保留原引用关系并记录版本。',
  emptyHint: '没有符合筛选条件的素材。',
  seedKey: 'assets',
}
